import React from 'react';
import OptimizedImage from './OptimizedImage';

// Title-case words: "new york" → "New York"
const capitalizeWords = (str) => {
  if (!str) return str;
  return str.replace(/\b\w/g, char => char.toUpperCase());
};

function TherapistCard({ 
  therapist, 
  onViewProfile = () => {}
}) {
  // Function to truncate bio text
  const truncateBio = (text, maxLength = 150) => {
    if (!text) return 'Professional therapist providing quality mental health services.';
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength).trim() + '...';
  };

  const handleProfileClick = () => {
    // Track profile click (fire-and-forget, don't block navigation)
    fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3001'}/api/analytics/profile-click`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        therapistId: therapist._id,
        source: 'search'
      }),
    }).catch(() => {});

    // Call the original handler
    onViewProfile(therapist);
  };

  return (
    <div className="bg-white rounded-2xl border border-[#254e70]/10 p-4 hover:shadow-lg transition-all duration-200">
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 items-start">

        {/* Profile Photo */}
        <div className="lg:col-span-1">
          <div className="relative max-w-[200px] mx-auto lg:mx-0 cursor-pointer" onClick={handleProfileClick}>
            <OptimizedImage 
              src={therapist.profilePhoto} 
              alt={`${therapist.firstName} ${therapist.lastName}`}
              className="w-full aspect-square rounded-xl"
              loading="lazy"
              sizes="(max-width: 768px) 200px, 200px"
              fallbackIcon={
                <svg className="w-16 h-16 text-gray-400" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 3c1.66 0 3 1.34 3 3s-1.34 3-3 3-3-1.34-3-3 1.34-3 3-3zm0 14.2c-2.5 0-4.71-1.28-6-3.22.03-1.99 4-3.08 6-3.08 1.99 0 5.97 1.09 6 3.08-1.29 1.94-3.5 3.22-6 3.22z"/>
                </svg>
              }
            />
            
            {/* Verification Badge */}
            {therapist.verification?.isVerified && (
              <div className="absolute -top-2 -right-2 bg-[#254e70] text-white p-2 rounded-full shadow-lg border-2 border-white">
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
              </div>
            )}
            
            {/* Availability Status - Over Image */}
            <div className="absolute bottom-2 left-2">
              {therapist.acceptingNewClients !== false ? (
                <span className="inline-flex items-center gap-1 px-2 py-1 bg-[#e4fde1] text-[#254e70] rounded-full text-xs font-medium border border-[#254e70]/20 shadow-sm">
                  <div className="w-1.5 h-1.5 bg-[#254e70] rounded-full animate-pulse"></div>
                  Accepting clients
                </span>
              ) : (
                <span className="inline-flex items-center gap-1 px-2 py-1 bg-[#254e70]/5 text-[#254e70]/60 rounded-full text-xs font-medium border border-[#254e70]/10 shadow-sm">
                  <div className="w-1.5 h-1.5 bg-[#254e70]/30 rounded-full"></div>
                  Waitlist only
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Main Info */}
        <div className="lg:col-span-2 space-y-3">
          <div>
            <h3 className="text-xl font-medium text-[#254e70] mb-1" style={{ fontFamily: 'Kanit, sans-serif' }}>
              <span className="cursor-pointer hover:text-[#9dacff] transition-colors duration-200" onClick={handleProfileClick}>
                {therapist.firstName} {therapist.lastName}
              </span>
              {therapist.pronouns && (
                <span className="text-sm font-normal text-[#254e70]/70 ml-2">({therapist.pronouns})</span>
              )}
              {therapist.credentials && therapist.credentials.length > 0 && (
                <span className="ml-2 text-sm font-normal text-[#254e70]/80">
                  {therapist.credentials.join(', ')}
                </span>
              )}
            </h3>
            <p className="text-sm text-[#254e70]/70 mb-1" style={{ fontFamily: 'Nunito Sans, sans-serif' }}>
              {therapist.professionalType || therapist.title || 'Licensed Therapist'}
            </p>
          </div>

          {/* Location and Experience Info */}
          <div className="flex flex-wrap items-center gap-3 text-xs">
            {therapist.location?.city && therapist.location?.state && (
              <div className="flex items-center gap-1">
                <svg className="w-3 h-3 text-[#9dacff]" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                </svg>
                <span className="text-[#254e70]/70">{capitalizeWords(therapist.location.city)}, {therapist.location.state}</span>
              </div>
            )}
            
            {therapist.professional?.yearsExperience && (
              <div className="flex items-center gap-1">
                <svg className="w-3 h-3 text-[#9dacff]" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                </svg>
                <span className="text-[#254e70]/70">{therapist.professional.yearsExperience}</span>
              </div>
            )}
            
            {therapist.distance && (
              <div className="flex items-center gap-1">
                <svg className="w-3 h-3 text-[#9dacff]" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M3 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z" clipRule="evenodd" />
                </svg>
                <span className="text-[#254e70]/70">{therapist.distance} mi away</span>
              </div>
            )}
          </div>

          {/* Specializations */}
          {therapist.specializations && therapist.specializations.length > 0 && (
            <div className="flex flex-wrap gap-1.5">
              {therapist.specializations.slice(0, 3).map((specialty, index) => (
                <span 
                  key={index}
                  className="bg-[#e4fde1] text-[#254e70] px-2 py-1 rounded-full text-xs font-medium"
                  style={{ fontFamily: 'Nunito Sans, sans-serif' }}
                >
                  {typeof specialty === 'object' ? specialty.name : specialty}
                </span>
              ))}
              {therapist.specializations.length > 3 && (
                <span className="bg-[#e4fde1] text-[#254e70] px-2 py-1 rounded-full text-xs font-medium">
                  +{therapist.specializations.length - 3} more
                </span>
              )}
            </div>
          )}

          {/* Key Info Section */}
          <div className="space-y-2">
            {/* Online Services */}
            {therapist.serviceAreas?.virtualOnly && (
              <div className="flex items-center gap-1">
                <svg className="w-3 h-3 text-blue-500" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M3 4a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-1 1H4a1 1 0 01-1-1V4zm0 4a1 1 0 011-1h12a1 1 0 011 1v6a1 1 0 01-1 1H4a1 1 0 01-1-1V8zm2 2a1 1 0 000 2h.01a1 1 0 100-2H5zm3 0a1 1 0 000 2h.01a1 1 0 100-2H8zm3 0a1 1 0 000 2h.01a1 1 0 100-2H11z" clipRule="evenodd" />
                </svg>
                <span className="text-xs text-blue-600 font-medium">
                  Online sessions
                </span>
              </div>
            )}

            {/* Insurance */}
            {(therapist.insurance?.acceptsInsurance || (therapist.insuranceAccepted && therapist.insuranceAccepted.length > 0)) && (
              <div className="flex items-center gap-1">
                <svg className="w-3 h-3 text-[#9dacff]" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M4 4a2 2 0 00-2 2v8a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2H4zm0 2h12v8H4V6z" clipRule="evenodd" />
                </svg>
                <span className="text-xs text-[#9dacff] font-medium">Accepts insurance</span>
              </div>
            )}

            {/* Languages */}
            {therapist.professional?.languages && therapist.professional.languages.length > 1 && (
              <div className="flex items-center gap-1">
                <svg className="w-3 h-3 text-[#9dacff]" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M7 2a1 1 0 011 1v1h3a1 1 0 110 2H9.578a18.87 18.87 0 01-1.724 4.78c.29.354.596.696.914 1.026a1 1 0 11-1.44 1.389c-.188-.196-.373-.396-.554-.6a19.098 19.098 0 01-3.107 3.567 1 1 0 01-1.334-1.49 17.087 17.087 0 003.13-3.733 18.992 18.992 0 01-1.487-2.494 1 1 0 111.79-.89c.234.47.489.928.764 1.372.417-.934.752-1.913.997-2.927H3a1 1 0 110-2h3V3a1 1 0 011-1zm6 6a1 1 0 01.894.553l2.991 5.982a.869.869 0 01.02.037l.99 1.98a1 1 0 11-1.79.895L15.383 16h-4.764l-.724 1.447a1 1 0 11-1.788-.894l.99-1.98.019-.038 2.99-5.982A1 1 0 0113 8zm-1.382 6h2.764L13 11.236 11.618 14z" clipRule="evenodd" />
                </svg>
                <span className="text-xs text-[#254e70]/70">
                  {therapist.professional.languages.slice(0, 2).join(', ')}
                  {therapist.professional.languages.length > 2 && ` +${therapist.professional.languages.length - 2} more`}
                </span>
              </div>
            )}
          </div>

          {/* Bio Section */}
          <p className="text-sm text-[#254e70]/70 leading-relaxed" style={{ fontFamily: 'Nunito Sans, sans-serif' }}>
            {truncateBio(therapist.bio)}
          </p>
        </div>

        {/* Action Section */}
        <div className="lg:col-span-1 flex flex-col h-full">
          {/* Badges - Top Right */}
          <div className="text-right mb-3 space-y-1">
            {therapist.rates?.freeConsultation?.offered && (
              <div>
                <span className="inline-flex items-center gap-1 bg-[#9dacff] text-white px-2 py-1 rounded-full text-xs font-semibold">
                  <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  Free consultation
                </span>
              </div>
            )}
          </div>

          <div className="flex-1"></div>

          {/* View Profile Button - Bottom */}
          <div className="mt-auto">
            <button
              onClick={handleProfileClick}
              className="w-full bg-[#254e70] text-white px-3 py-3 rounded-xl font-semibold hover:bg-[#1d3e5a] transition-colors text-sm" 
              style={{ fontFamily: 'Nunito Sans, sans-serif' }}
            >
              View Profile
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default TherapistCard; 